import './applicantEditIncomePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    additionalIncomeAmount,
    additionalIncomeDurationMonths,
    additionalIncomeDurationYears,
    additionalIncomeEmployerName,
    additionalIncomeFrequency,
    additionalIncomeJobTitle,
    additionalIncomeType,
    computed: { reviewEditPathname, reviewEditProgress },
    hasAdditionalIncome,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setAdditionalIncomeAmount,
    setAdditionalIncomeDurationMonths,
    setAdditionalIncomeDurationYears,
    setAdditionalIncomeEmployerName,
    setAdditionalIncomeFrequency,
    setAdditionalIncomeJobTitle,
    setAdditionalIncomeType,
    setHasAdditionalIncome,
    syncIncome,
  } = useApplicantStore();
  const {
    clearErrors,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      hasAdditionalIncomeValue: hasAdditionalIncome,
      incomeTypeValue: additionalIncomeType,
      incomeFrequencyValue: additionalIncomeFrequency,
      incomeAmountValue: toCurrencyString(additionalIncomeAmount),
      incomeEmployerNameValue: additionalIncomeEmployerName,
      incomeJobTitleValue: additionalIncomeJobTitle,
      incomeDurationYearsValue: additionalIncomeDurationYears,
      incomeDurationMonthsValue: additionalIncomeDurationMonths,
    },
  });

  const shouldShowIncomeFields = watch('hasAdditionalIncomeValue') === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  // Register options hand the change event straight through, not the value.
  const handleAdditionalIncomeChange = (event: {
    target: { value: string };
  }): void => {
    if (event.target.value !== YES) {
      // `formSubmit` discards these once the answer is No, so blank them rather
      // than leave a stale value behind the disabled fields.
      setValue('incomeAmountValue', '');
      setValue('incomeEmployerNameValue', '');
      setValue('incomeJobTitleValue', '');

      clearErrors([
        'incomeAmountValue',
        'incomeEmployerNameValue',
        'incomeJobTitleValue',
      ]);
    }
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setAdditionalIncomeAmount(incomeAmountValue);
      setAdditionalIncomeFrequency(incomeFrequencyValue);
      setAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setAdditionalIncomeDurationMonths(null);
        setAdditionalIncomeDurationYears(null);
        setAdditionalIncomeEmployerName(null);
        setAdditionalIncomeJobTitle(null);
      }
    } else {
      setAdditionalIncomeAmount(null);
      setAdditionalIncomeFrequency(null);
      setAdditionalIncomeType(null);
      setAdditionalIncomeDurationMonths(null);
      setAdditionalIncomeDurationYears(null);
      setAdditionalIncomeEmployerName(null);
      setAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page applicant-edit-income-page">
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditIncome.form.h1}>
                {t('ApplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditIncome.form.h2}>
                {t('ApplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantEditIncome.alert.error}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('hasAdditionalIncomeValue', {
                      ...additionalIncomeValidation,
                      onChange: handleAdditionalIncomeChange,
                    })}
                    error={errors.hasAdditionalIncomeValue}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .hasAdditionalIncomeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form
                        .hasAdditionalIncomeSelect
                    }
                    label={t('ApplicantEditIncomePage.additionalIncome')}
                    options={additionalIncomeTypeOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('incomeTypeValue', {
                      ...incomeTypeValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    error={errors.incomeTypeValue}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form.incomeTypeSelect
                    }
                    label={t('ApplicantEditIncomePage.incomeType')}
                    options={incomeTypeOptions}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('incomeEmployerNameValue', {
                        ...incomeEmployerNameValidation,
                        disabled: !shouldShowIncomeFields,
                      })}
                      data-testid={
                        locator.pages.applicantEditIncome.form.employerNameInput
                      }
                      error={errors.incomeEmployerNameValue}
                      errorTestId={
                        locator.pages.applicantEditIncome.form
                          .employerNameInputError
                      }
                      label={t('ApplicantEditIncomePage.employerName')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('incomeJobTitleValue', {
                        ...incomeJobTitleValidation,
                        disabled: !shouldShowIncomeFields,
                      })}
                      data-testid={
                        locator.pages.applicantEditIncome.form.jobTitleInput
                      }
                      error={errors.incomeJobTitleValue}
                      errorTestId={
                        locator.pages.applicantEditIncome.form
                          .jobTitleInputError
                      }
                      label={t('ApplicantEditIncomePage.jobTitle')}
                    />
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        {...register('incomeDurationYearsValue', {
                          ...incomeDurationYearsValidation,
                          disabled: !shouldShowIncomeFields,
                        })}
                        error={errors.incomeDurationYearsValue}
                        errorTestId={
                          locator.pages.applicantEditIncome.form.yearSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditIncome.form.yearSelect
                        }
                        label={t('ApplicantEditIncomePage.durationYears')}
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        {...register('incomeDurationMonthsValue', {
                          ...incomeDurationMonthsValidation,
                          disabled: !shouldShowIncomeFields,
                        })}
                        error={errors.incomeDurationMonthsValue}
                        errorTestId={
                          locator.pages.applicantEditIncome.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditIncome.form.monthSelect
                        }
                        label={t('ApplicantEditIncomePage.durationMonths')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('incomeFrequencyValue', {
                      ...incomeFrequencyValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    error={errors.incomeFrequencyValue}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form.frequencySelect
                    }
                    label={t('ApplicantEditIncomePage.incomeFrequency')}
                    options={incomeFrequencyOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('incomeAmountValue', {
                      ...incomeAmountValidation,
                      disabled: !shouldShowIncomeFields,
                    })}
                    data-testid={
                      locator.pages.applicantEditIncome.form.amountInput
                    }
                    error={errors.incomeAmountValue}
                    errorTestId={
                      locator.pages.applicantEditIncome.form.amountInputError
                    }
                    label={t('ApplicantEditIncomePage.incomeAmount')}
                    currency
                  />
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.applicantEditIncome.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditIncomePage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.applicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingIncome}
                    fullWidth
                    submit
                  >
                    {t('ApplicantEditIncomePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantEditIncome.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditIncomePage';
